import * as React from "react";

import { Header } from "./Header";
import { DeviceBar } from "./DeviceBar";
import { Diagram } from "./Diagram";

import { ProtectiveFunction } from "../models/ProtectiveFunction";
import { SFT_Targets } from "../models/SFT_Targets";
import { PT_ProtectiveFunction } from "../models/PT_ProtectiveFunction";
import { countrydetails } from "../models/countrydetails";
import { DiagramData } from "../models/DiagramData";
import { HFTResults } from "../models/HFTResults";
import { DeviceData } from "../models/DeviceData";
import { DiagramApiController } from "../models/DiagramApiController.generated";
import { SaveCCFVotingParams } from "../models/SaveCCFVotingParams";
import { ChangeRedundancyObject } from "../models/ChangeRedundancyObject";
import { TargetSpecUpdateParams } from "../models/TargetSpecUpdateParams";
import { pfdToSil, votingToConfigType } from "../helpers";
import { votingConfigByLocation } from "../psfHelpers";
import { DM_DataSheet } from "../models/DM_DataSheet";
import { ManualInputs } from "../models/ManualInputs";
import { ManualAction } from "../models/ManualAction";
import { PSF_ProtectiveFunctionComments } from "../models/PSF_ProtectiveFunctionComments";
import { AddConfigArgs } from "../models/AddConfigArgs";
import { ConfigurationType } from "../models/ConfigurationType";
import { BindAndSaveDeviceArgs } from "../models/BindAndSaveDeviceArgs";
import { ProtectiveFunctionLocation } from "../models/protectiveFunctionLocation";
import { BindAndSaveLogicArgs } from "../models/BindAndSaveLogicArgs";
import { ClickedDeviceInfo } from "../models/clickedDeviceInfo";
import { DeviceBarDevices } from "../models/DeviceBarDevices";
import { UpdateLogicSolverDataArgs } from "../models/UpdateLogicSolverDataArgs";
import { DeleteDeviceArgs } from "../models/DeleteDeviceArgs";
import { LSM_LogicSolverSheet } from "../models/LSM_LogicSolverSheet";
import { DeleteCustLogicDeviceArgs } from "../models/DeleteCustLogicDeviceArgs";
import { SS_SupportSystemSheet } from "../models/SS_SupportSystemSheet";
import { uploadCustomDeviceImage } from "../api2";
import {
  ProtectiveFunctionWrapper,
  Message
} from "../models/ProtectiveFunctionWrapper";
import { PositionPasteAndSaveDeviceArgs } from "../models/PositionPasteAndSaveDeviceArgs";
import { DeviceBarDevicesAndPfString } from "../models/DeviceBarDevicesAndPfString";
import { SilModal } from "./SilModal";
import { CalculationResults } from "../models/CalculationResults";
import { PM_ProtectiveFunction } from "../models/PM_ProtectiveFunction";
import { UpdateDeviceSheetDataParams } from "../models/UpdateDeviceSheetDataParams";
import { MasterPoolMismatchItem } from "../models/MasterPoolMismatchItem";
import { DeviceSheetTypes } from "../models/deviceSheeTypes";
import { UserDetails } from "../models/UserDetails";
import { Area } from "../models/Area";

interface Props {
  protectiveFunctionId: number;
  sessionTimeout: number;
}

interface State {
  copiedBoundDevice?: DeviceData;
  loading: boolean;
  zoom: number;
  protectiveFunctionsInProject: number[];
  protectiveFunction: ProtectiveFunction;
  psfProtectiveFunctionComments: PSF_ProtectiveFunctionComments;
  sftTargets?: SFT_Targets;
  devices: DeviceData[]; // devices used in the PF
  deviceBarDevices: DeviceBarDevices;
  calculations?: CalculationResults;
  pfdPieChartData: countrydetails[];
  strPieChartData: countrydetails[];
  calculationResults?: PT_ProtectiveFunction;
  hftResults?: HFTResults;
  manualInputs?: ManualInputs;
  manualAction?: ManualAction;
  deviceRegister?: ClickedDeviceInfo;
  // []: calculation completed successfully
  // [errors]: calculations completed with errors
  // undefined: user is not currently looking at errors, or a successfully completed window
  errorsAndWarnings?: Message[];
  showErrorsAndWarningsModal?: boolean;
  supportConfigPasteState: {
    ss1: boolean;
    ss2: boolean;
  };
  customDeviceUsageInfo?: {
    function: string;
    deviceType: "device" | "logic solver" | "support system";
  };
  userName: string;
  scale: number;
  secondsUntilExpiration: number;
  updateSecondsUntilExpirationTimer: number;
  exitAppTimer?: number;
  isRefreshing: boolean;
  masterPoolMismatchItems: MasterPoolMismatchItem[];
  isLocked: boolean;
  readOnly: boolean;
  clientId?: number;
  editAccessIntervalId: number;
  userDetails?: UserDetails;
  area: Area;
}

export class DiagramContainer extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props);
    this.state = {
      loading: true,
      zoom: 0.5,
      protectiveFunctionsInProject: [],
      protectiveFunction: new ProtectiveFunction(),
      psfProtectiveFunctionComments: new PSF_ProtectiveFunctionComments(),
      devices: [],
      deviceBarDevices: new DeviceBarDevices(),
      pfdPieChartData: [
        new countrydetails(),
        new countrydetails(),
        new countrydetails(),
        new countrydetails()
      ],
      strPieChartData: [
        new countrydetails(),
        new countrydetails(),
        new countrydetails(),
        new countrydetails()
      ],
      supportConfigPasteState: {
        ss1: false,
        ss2: false
      },
      userName: "",
      readOnly: true,
      scale: 1,
      secondsUntilExpiration: props.sessionTimeout,
      updateSecondsUntilExpirationTimer: setInterval(this.updateSecondsUntilExpirationInterval, 1000),
      isRefreshing: false,
      isLocked: false,
      editAccessIntervalId: setInterval(this.checkEditAccess, 1000 * 60), //load diagram data each minute
      masterPoolMismatchItems: [],
      area: new Area()
    };
  }

  handleUserInteraction = () => {
    const timeElapsed = this.props.sessionTimeout - this.state.secondsUntilExpiration;
    if (timeElapsed >= this.props.sessionTimeout - 60) {
      // too late to automatically reset session
      // user must use window to reset session
    }
    else if (timeElapsed >= this.props.sessionTimeout / 2) {
      if (!this.state.isRefreshing) {
        this.setState({ isRefreshing: true });
        // auto reset session
        DiagramApiController.getDiagramData(this.props.protectiveFunctionId)
          .then(dd => {
            this.setDiagramData(dd);
            this.setState({ isRefreshing: false });
          });
      }
    }
  }

  updateSecondsUntilExpirationInterval = () => {
    this.setState({ secondsUntilExpiration: this.state.secondsUntilExpiration - 1 });
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    if (this.state.secondsUntilExpiration - 1 == nextState.secondsUntilExpiration) {
      return false;
    }
    return true;
  }

  resetTimers = () => {
    console.log("resetting timers");
    if (this.state.exitAppTimer) {
      clearTimeout(this.state.exitAppTimer);
    }

    this.setState({
      secondsUntilExpiration: this.props.sessionTimeout,
      exitAppTimer: setTimeout(() => window.location.href = "/Login.aspx", this.props.sessionTimeout * 1000)
    });
  }

  setDiagramData = (dd: DiagramData) => {
    this.resetTimers();
    this.setState({
      loading:false,
      protectiveFunctionsInProject: dd.protectiveFunctionsInProject,
      protectiveFunction: dd.protectiveFunction,
      sftTargets: dd.sftTargets,
      devices: dd.devices,
      psfProtectiveFunctionComments: dd.psF_ProtectiveFunctionComments,
      pfdPieChartData: dd.pfdPieChartData,
      strPieChartData: dd.strPieChartData,
      calculationResults: dd.calculationResults,
      hftResults: dd.hftResults,
      manualInputs: dd.manualInputs,
      manualAction: dd.manualAction,
      userName: dd.userName,
      masterPoolMismatchItems: dd.masterPoolMismatchItems,
      readOnly: !dd.writeRight,
      isLocked: dd.protectiveFunction.isLocked,
      clientId: dd.clientId,
      userDetails: dd.userDetails,
      area: dd.area
    });
    if (dd.showLockOutNotice)
    {
      this.showReadOnlyMessage();
    }    
  };

  componentDidMount() {
    DiagramApiController.getDiagramData(this.props.protectiveFunctionId)
      .then(dd => {
        this.setDiagramData(dd);
        DiagramApiController.getDeviceList(dd.protectiveFunction.projectId_PrtId)
          .then(deviceBarDevices => this.setState({ deviceBarDevices }));
      });
    
    window.addEventListener("beforeunload", this.handleWindowBeforeUnload);
  }
  
  componentWillUnmount(): void {
    window.removeEventListener("beforeunload", this.handleWindowBeforeUnload);
  }

  handleWindowBeforeUnload = () => {
    clearInterval(this.state.editAccessIntervalId);
    DiagramApiController.releaseProtectiveFunctionLock(this.props.protectiveFunctionId);
  }

  checkEditAccess = () => {
    DiagramApiController.getDiagramData(this.props.protectiveFunctionId)
      .then(dd => {
        this.setDiagramData(dd);
      });
  }
  
  showReadOnlyMessage() {
    this.setState({
      showErrorsAndWarningsModal: true,
      errorsAndWarnings: [
        new Message("Save Failed", 'readonly')
      ]})
  }

  handleConfigurationClick = (      
    newConfig: string,
    configType: ConfigurationType
  ) => {    
    if (this.state.isLocked) { return this.showReadOnlyMessage(); } 
    if (this.state.readOnly) { return; }
    
    const args = new AddConfigArgs();
    args.protectiveFunctionId = this.props.protectiveFunctionId;
    args.configuration = newConfig;
    args.configType = configType;

    DiagramApiController.addConfiguration(args).then(dd =>
      this.setDiagramData(dd)
    );
  };

  // Handle Device Bar Device Click
  handleDeviceClick = (deviceInfo: ClickedDeviceInfo) => {
    if (this.state.readOnly) { return; }
    this.setState({ deviceRegister: deviceInfo });
  };

  handleSupportConfigurationClick = () => {
    if (this.state.readOnly) { return; }

    const supportConfigPasteState = { ...this.state.supportConfigPasteState };
    const support1IsEmpty =
      this.state.devices.filter(
        d => d.ptProtectiveFunction.inputs.trim() === "SSO1"
      )[0] == null;
    const support2IsEmpty =
      this.state.devices.filter(
        d => d.ptProtectiveFunction.inputs.trim() === "SSO2"
      )[0] == null;
    if (!supportConfigPasteState.ss1 && support1IsEmpty) {
      supportConfigPasteState.ss1 = true;
    } else if (!supportConfigPasteState.ss2 && support2IsEmpty) {
      supportConfigPasteState.ss2 = true;
    }

    this.setState({ supportConfigPasteState });
  };

  // Handle Device Location Click (to fill empty spot in configuration for placing devices)

  handleEmptyDeviceLocationClick = (
    position: string,
    location: ProtectiveFunctionLocation,
    voting: string
  ) => {
    if (this.state.readOnly) { return; }
    if (!this.state.deviceRegister.isDevice()) { return; }
    if (this.state.isLocked) { return this.showReadOnlyMessage(); }
    
    const args: BindAndSaveDeviceArgs = {
      configType: votingToConfigType(voting),
      dataSheetId: this.state.deviceRegister.deviceDataSheetId,
      devicePosition: position,
      protectiveFunctionId: this.props.protectiveFunctionId,
      input: location,
      source: this.state.deviceRegister.deviceId
    };

    return DiagramApiController.bindAndSaveDevice(args).then(dd =>
      this.setDiagramData(dd)
    );
  };

  pasteConfiguration = (copiedLocation, pastedLocation) => {
    if (this.state.readOnly) { return; }
    if (this.state.isLocked) { return this.showReadOnlyMessage(); }

    console.log("pasting config");
    DiagramApiController.positionPasteAndSaveDevice({
      protectiveFunctionId: this.props.protectiveFunctionId,
      copiedConfigLocation: copiedLocation,
      pastedConfigLocation: pastedLocation
    }).then(dd => this.setDiagramData(dd));
  };

  promoteToClientLevel = (deviceType: DeviceSheetTypes, sheetId: number) => {
    if (this.state.readOnly) { return Promise.resolve(); }
    if (this.state.isLocked) {
      this.showReadOnlyMessage();
      return Promise.resolve();
      }
      const protectiveFunctionId = this.props.protectiveFunctionId;

      return DiagramApiController.promoteDeviceToClientLevel({ deviceType, sheetId, protectiveFunctionId })
      .then(deviceBarDevices => {
        if (deviceBarDevices != null) {
          this.setState({ deviceBarDevices })
        }
      });
  }

  addSupportSystem = (location: 1 | 2) => {    
    if (this.state.readOnly) { return; }
    if (!this.state.deviceRegister.isSupportSystem()) { return; }
    if (this.state.isLocked) return this.showReadOnlyMessage();

    DiagramApiController.bindAndSaveSupport({
      protectiveFunctionId: this.props.protectiveFunctionId,
      deviceId: this.state.deviceRegister.deviceId,
      deviceSheetId: this.state.deviceRegister.supportSheetId,
      supportLocation: location
    }).then(dd => this.setDiagramData(dd));
  };

  handleEmptyLogicDeviceLocationClick = () => {
    if (this.state.readOnly) { return; }
    if (!this.state.deviceRegister.isLogicSolver()) { return; }
    if (this.state.isLocked) return this.showReadOnlyMessage();
    
    const args = new BindAndSaveLogicArgs();
    args.deviceId = this.state.deviceRegister.deviceId;
    args.pid = this.props.protectiveFunctionId;
    args.logicSolverSheetId = this.state.deviceRegister.logicSolverSheetId;

    DiagramApiController.bindAndSaveLogic(args).then(dd =>
      this.setDiagramData(dd)
    );
  };

  handleUpdateDeviceSheetData = (
    ptProtectiveFunction: PT_ProtectiveFunction,
    pmProtectiveFunction: PM_ProtectiveFunction,
  ) => {
    if (this.state.readOnly) { return; }
    if (this.state.isLocked) return new Promise<void>(() => this.showReadOnlyMessage());
    return DiagramApiController.updateDeviceSheetData({
      ptProtectiveFunction,
      pmProtectiveFunction,
    }).then(dd => this.setDiagramData(dd));
  };

  handleUpdateLogicSolverData = (args: UpdateLogicSolverDataArgs) => {
    if (this.state.readOnly) { return; }
    if (this.state.isLocked) return new Promise<boolean>(() => {
      this.showReadOnlyMessage();
      return false;
    });
    return DiagramApiController.updateLogicSolverData(args).then(dd => {
      if (dd) {
        this.setDiagramData(dd);
        return true;
      } else {
        return false;
      }
    });
  };

  handleUpdateCcf = (updatedPSF: PSF_ProtectiveFunctionComments) => {
    if (this.state.readOnly) { return; }
    if (this.state.isLocked) return new Promise<void>(() => this.showReadOnlyMessage());
    const params = new SaveCCFVotingParams();
    params.protectiveFunctionId = this.props.protectiveFunctionId;
    params.updatedPsf = updatedPSF;

    return DiagramApiController.saveCCFVoting(params).then(diagram =>
      this.setDiagramData(diagram)
    );
  };

  handleForwardOrBackButtonPressed = (direction: "Back" | "Forward") => {
    const currentPfId = parseInt(this.props.protectiveFunctionId.toString());
    const currentPfIdIndex = this.state.protectiveFunctionsInProject.indexOf(
      currentPfId
    );
    if (currentPfIdIndex === -1) {
      // Something went wrong, this PF should always be in this list
      return;
    }

    // There is probably a more elegant way to handle this but trying to move as fast as possible
    let nextPfIdIndex = undefined;
    if (direction === "Forward") {
      nextPfIdIndex = currentPfIdIndex + 1;
      if (nextPfIdIndex >= this.state.protectiveFunctionsInProject.length) {
        nextPfIdIndex = 0;
      }
    } else {
      nextPfIdIndex = currentPfIdIndex - 1;
      if (nextPfIdIndex < 0) {
        nextPfIdIndex = this.state.protectiveFunctionsInProject.length - 1;
      }
    }

    window.location.href = `/engineering/diagram?protectiveFunctionId=${
      this.state.protectiveFunctionsInProject[nextPfIdIndex]
    }`;
  };

  handleZoomButtonsClicked = (button: "Zoom In" | "Zoom Out" | "Reset") => {
    switch (button) {
      case "Zoom In":
        this.setState({ scale: this.state.scale * 1.1 });
        break;
      case "Zoom Out":
        this.setState({ scale: this.state.scale / 1.1 });
        break;
      case "Reset":
        this.setState({ scale: 1 });
        break;
    }
  };

  handleExitButtonClicked = async () => {
    const projectId = this.state.protectiveFunction.projectId_PrtId;
    if (!this.state.readOnly) {
      DiagramApiController.calculation(this.props.protectiveFunctionId);
    }
    DiagramApiController.releaseProtectiveFunctionLock(this.props.protectiveFunctionId);
    window.location.href = `/ProjectEdit.aspx?ProjectId=${projectId}`;
  };

  handleRestoreDeviceSheetData = async (ptProtectiveFunctionId: number) => {
    const device = this.state.devices.find(x => x.ptProtectiveFunction.protectiveFunctionID == ptProtectiveFunctionId);
    await DiagramApiController.restoreDeviceSheetData(
      ptProtectiveFunctionId,
      device.ptProtectiveFunction.protectiveId_FK
    ).then(results => {
      this.setDiagramData(results);
    });
  };

  handleCalculateButtonClicked = () => {
    if (this.state.readOnly) { return; }
    if (this.state.isLocked) return this.showReadOnlyMessage();
    
    const wrapper = new ProtectiveFunctionWrapper(
      this.state.devices,
      this.state.psfProtectiveFunctionComments,
      this.state.supportConfigPasteState
    );
    const messages = wrapper.getPreCalcMessages();
    console.log(messages);

    return DiagramApiController.calculation(
      this.props.protectiveFunctionId
    ).then(results => {
      this.setDiagramData(results.diagramData);
      const postWrapper = new ProtectiveFunctionWrapper(
        this.state.devices,
        this.state.psfProtectiveFunctionComments,
        this.state.supportConfigPasteState
      );
      postWrapper.addMasterPoolMismatchItems(results.diagramData.masterPoolMismatchItems);
      const postCalcMessages = postWrapper.getPostCalcMessages();
      console.log(postCalcMessages);
      var allMessages = messages.concat(postCalcMessages);
      this.setState({ 
          errorsAndWarnings: allMessages,
          showErrorsAndWarningsModal: true
      });
    });
  };

  handleTargetSpecUpdate = (target_pfd: number, target_mttf: number) => {
    if (this.state.readOnly) { return; }
    if (this.state.isLocked) { return this.showReadOnlyMessage(); }
    
    // 1) compose SFT_Targets object to send to API controller
    const targetSpecUpdateParams = new TargetSpecUpdateParams();
    targetSpecUpdateParams.pfDavg = target_pfd;
    targetSpecUpdateParams.mttf = target_mttf;
    targetSpecUpdateParams.sil = pfdToSil(target_pfd);
    targetSpecUpdateParams.pId = this.state.protectiveFunction.protectiveId;

    // 2) When API controller returns, it will return the full DiagramData object
    //    which should now have sft targets

    DiagramApiController.saveSFT_Targets(targetSpecUpdateParams).then(dd =>
      this.setDiagramData(dd)
    );
  };

  deviceUpdated = (ptProtectiveFunction: PT_ProtectiveFunction) => {
    if (this.state.readOnly) { return; }

    // find the updated device in the device list
    const deviceIndex = this.state.devices.findIndex(device => {
      return (
        device.ptProtectiveFunction.protectiveFunctionID ===
        ptProtectiveFunction.protectiveFunctionID
      );
    });
    if (deviceIndex !== -1) {
      const devices = [...this.state.devices];
      devices[deviceIndex].ptProtectiveFunction = ptProtectiveFunction;
      this.setState({ devices });
    }
  };

  handleProcessHazardUpdate = () => {
    console.log(
      "I'm handling the process hazard update from the diagram container."
    );
  };

  handleInputRedundancyChange = (newRedundant: "NREN" | "REN") => {
    if (this.state.readOnly) { return; }
    if (this.state.isLocked) return new Promise<void>(() => this.showReadOnlyMessage());
    
    const params = new ChangeRedundancyObject();
    params.protectiveId = this.props.protectiveFunctionId;
    params.redundant = newRedundant;

    return DiagramApiController.changeProtectiveFunctionRedundancy(params).then(
      dd => this.setDiagramData(dd)
    );
  };

  // Handle manual inputs save
  handleManualInputsSave = (manualInputs: ManualInputs) => {
    if (this.state.readOnly) { return; }
    if (this.state.isLocked) return this.showReadOnlyMessage();    
    DiagramApiController.saveManualInputs(
      this.props.protectiveFunctionId,
      manualInputs
    ).then(dd => this.setDiagramData(dd));
  };

  // Handle manual actions save
  handleManualActionSave = (manualAction: ManualAction) => {
    if (this.state.readOnly) { return; }
    if (this.state.isLocked) return this.showReadOnlyMessage();

    DiagramApiController.saveManualAction(
      this.props.protectiveFunctionId,
      manualAction
    ).then(dd => this.setDiagramData(dd));
  };

  // Create manual input
  createManualInput = () => {
    if (this.state.readOnly) { return; }
    if (this.state.isLocked) return this.showReadOnlyMessage();
    DiagramApiController.createManualInputs(
      this.props.protectiveFunctionId
    ).then(dd => this.setDiagramData(dd));
  };

  // Create manual action
  createManualAction = () => {
    if (this.state.readOnly) { return; }
    if (this.state.isLocked) { return this.showReadOnlyMessage(); }
    DiagramApiController.createManualAction(
      this.props.protectiveFunctionId
    ).then(dd => this.setDiagramData(dd));
  };

  deleteDevice = (device: DeviceData) => {
    if (this.state.readOnly) { return; }
    if (this.state.isLocked) { return new Promise<void>(() => this.showReadOnlyMessage()); }
    if (
      this.state.copiedBoundDevice &&
      this.state.copiedBoundDevice.ptProtectiveFunction &&
      this.state.copiedBoundDevice.ptProtectiveFunction.protectiveFunctionID ===
        device.ptProtectiveFunction.protectiveFunctionID
    ) {
      // If the same device is also "copied" we want to remove it from the copy register also. The server currently doesn't
      // handle this very well. Shouldn't be a tough fix but put it on the TODO list.
      this.setState({ copiedBoundDevice: null });
    }

    return DiagramApiController.deleteDevice({
      protectiveFunctionId: this.props.protectiveFunctionId,
      ptProtectiveFunctionId: device.ptProtectiveFunction.protectiveFunctionID
    }).then(dd => this.setDiagramData(dd));
  };

  copyBoundDevice = (device: DeviceData) => {
    this.setState({ copiedBoundDevice: device });
  };

  pasteBoundDevice = (
    position: string,
    location: ProtectiveFunctionLocation
  ) => {
    if (this.state.readOnly) { return Promise.resolve(); }
    if (this.state.copiedBoundDevice == null) { return Promise.resolve(); }
    if (this.state.isLocked) { return new Promise<void>(() => this.showReadOnlyMessage()); }
    const copiedDevice: DeviceData = { ...this.state.copiedBoundDevice };

    const voting = votingConfigByLocation(location, this.state.psfProtectiveFunctionComments);
    const configType = votingToConfigType(voting);

    return DiagramApiController.bindPasteAndSaveDevice({
      destinationConfiguration: location,
      destinationLocation: position,
      deviceId: copiedDevice.ptProtectiveFunction.deviceId,
      protectiveFunctionId: this.props.protectiveFunctionId,
      ptProtectiveFunctionId: copiedDevice.ptProtectiveFunction.protectiveFunctionID,
      destinationConfigType: configType
    }).then(dd => this.setDiagramData(dd));
  };

  // Delete manual input
  deleteManualInput = () => {
    if (this.state.readOnly) { return; }
    if (this.state.isLocked) { return this.showReadOnlyMessage(); }
    DiagramApiController.deleteManualInputs(
      this.props.protectiveFunctionId
    ).then(dd => this.setDiagramData(dd));
  };

  // Delete manual action
  deleteManualAction = () => {
    if (this.state.readOnly) { return; }
    if (this.state.isLocked) { return this.showReadOnlyMessage(); }
    DiagramApiController.deleteManualAction(
      this.props.protectiveFunctionId
    ).then(dd => this.setDiagramData(dd));
  };

  // Delete configuration
  deleteConfiguration = (configurationLocation: ProtectiveFunctionLocation) => {
    if (this.state.readOnly) { return; }
    if (this.state.isLocked) { return this.showReadOnlyMessage(); }

    DiagramApiController.position_DeleteDevice({
      protectiveFunctionId: this.props.protectiveFunctionId,
      positionid: configurationLocation,
      devices: this.state.devices
        .filter(
          d => d.ptProtectiveFunction.inputs.trim() === configurationLocation
        )
        .map(d => d.ptProtectiveFunction.protectiveFunctionID)
    }).then(dd => this.setDiagramData(dd));
    if (configurationLocation === "SSO1" || configurationLocation === "SSO2") {
      const state = { ... this.state.supportConfigPasteState };
      if (configurationLocation === "SSO1") {
        state.ss1 = false;
      } else {
        state.ss2 = false;
      }

      this.setState({ supportConfigPasteState: state });
    }
    else {
      DiagramApiController.position_DeleteDevice({
        protectiveFunctionId: this.props.protectiveFunctionId,
        positionid: configurationLocation,
        devices: this.state.devices
          .filter(
            d => d.ptProtectiveFunction.inputs.trim() === configurationLocation
          )
          .map(d => d.ptProtectiveFunction.protectiveFunctionID)
      }).then(dd => this.setDiagramData(dd));
    }
  };

  handleUpgradeButtonClick = () => {
    if (this.state.readOnly) { return; }
    if (this.state.isLocked) { return this.showReadOnlyMessage(); }
    DiagramApiController.upgradeProtectiveFunction(
      this.props.protectiveFunctionId
    ).then(dd => this.setDiagramData(dd));
  };

  // Delete Customer Logic Device
  deleteCustomLogic = (lsm_logicSheet: LSM_LogicSolverSheet) => {
    if (this.state.readOnly) { return; }
    if (this.state.isLocked) return this.showReadOnlyMessage();
    const projectId = this.state.protectiveFunction.projectId_PrtId;

    DiagramApiController.deleteCustomLogic(lsm_logicSheet, projectId).then(
      resp => this.setState({
        deviceBarDevices: resp.deviceBarDevices,
        customDeviceUsageInfo: {
          function: resp.pt_ProtectiveFunction,
          deviceType: "logic solver"
        }
      })
    );
  };

  // Delete Customer Device
  deleteCustomDevice = (dm_dataSheet: DM_DataSheet) => {
    if (this.state.readOnly) { return; }
    if (this.state.isLocked) return this.showReadOnlyMessage();
    const projectId = this.state.protectiveFunction.projectId_PrtId;
    DiagramApiController.deleteCustomDeviceSheet(dm_dataSheet, projectId).then(
      resp => this.setState({
        deviceBarDevices: resp.deviceBarDevices,
        customDeviceUsageInfo: {
          function: resp.pt_ProtectiveFunction,
          deviceType: "device"
        }
      })
    );
  };

  // Wipe Custom Device PT_Protective Function string (string only set when users tries to delete custom device that is in a protective function)
  wipeCustomDevicePt_ProtectiveFunction = () => {
    this.setState({customDeviceUsageInfo: null})
  }

  // Delete Customer Support Device
  deleteCustomSupport = (ss_supportSystemSheet: SS_SupportSystemSheet) => {
    if (this.state.readOnly) { return; }
    if (this.state.isLocked) return this.showReadOnlyMessage();
    const projectId = this.state.protectiveFunction.projectId_PrtId;
    DiagramApiController.deleteCustomSupport(ss_supportSystemSheet, projectId).then(
      resp => this.setState({
        deviceBarDevices: resp.deviceBarDevices,
        customDeviceUsageInfo: {
          function: resp.pt_ProtectiveFunction,
          deviceType: "support system"
        }
      })
    );
  };

  // Create Custom Device
  saveCustomDeviceSheet = (dm_dataSheet: DM_DataSheet, file: File) => {
    if (this.state.readOnly) { return; }
      if (this.state.isLocked) { return this.showReadOnlyMessage(); }
    const projectId = this.state.protectiveFunction.projectId_PrtId;
    DiagramApiController.saveCustomDeviceSheet(dm_dataSheet, projectId).then(
      async deviceBarDevices => {
        await uploadCustomDeviceImage("DEVICE", file, dm_dataSheet.deviceId);
        this.setState({ deviceBarDevices });
      }
    );
  };

  // Create Custom Logic Device
  saveCustomLogicSolver = ( lsm_LogicSolverSheet: LSM_LogicSolverSheet, file: File ) => {
    if (this.state.readOnly) { return; }
    if (this.state.isLocked) { return this.showReadOnlyMessage(); }
    const projectId = this.state.protectiveFunction.projectId_PrtId;
    DiagramApiController.saveCustomLogicSolver(lsm_LogicSolverSheet, projectId).then(
      async deviceBarDevices => {
        await uploadCustomDeviceImage( "LS", file, lsm_LogicSolverSheet.deviceID );
        this.setState({ deviceBarDevices });
      }
    );
  };

  // Create Custom Support System
  saveCustomSupportSystem = ( ss_supportSystemSheet: SS_SupportSystemSheet, file: File ) => {
    if (this.state.readOnly) { return; }
    if (this.state.isLocked) { return this.showReadOnlyMessage(); }
    const projectId = this.state.protectiveFunction.projectId_PrtId;
    DiagramApiController.saveCustomSupportSystem(ss_supportSystemSheet, projectId).then(
      async deviceBarDevices => {
        await uploadCustomDeviceImage( "SS", file, ss_supportSystemSheet.supportSystemID );
        this.setState({ deviceBarDevices });
      }
    );
  };

  // Save SRS Notes
  saveSrsNotes = (notes: string, srsType: string) => {
    console.log(notes, srsType);
    if (this.state.readOnly) { return; }
    if (this.state.isLocked) { return this.showReadOnlyMessage(); }
    
    var psf_ProtectiveFunctionComments = this.state
      .psfProtectiveFunctionComments;

    switch (srsType) {
      case "Process Hazard": {
        psf_ProtectiveFunctionComments.processHazard = notes;
        DiagramApiController.saveProcessHazard(
          psf_ProtectiveFunctionComments
        ).then(dd => this.setDiagramData(dd));
        break;
      }
      case "Description": {
        psf_ProtectiveFunctionComments.sourceDescription = notes;
        DiagramApiController.saveDescription(
          psf_ProtectiveFunctionComments
        ).then(dd => this.setDiagramData(dd));
        break;
      }
      case "Diagnostics": {
        psf_ProtectiveFunctionComments.sourceDiagnostics = notes;
        DiagramApiController.saveDiagnostics(
          psf_ProtectiveFunctionComments
        ).then(dd => this.setDiagramData(dd));
        break;
      }
      case "Reset": {
        psf_ProtectiveFunctionComments.resetFunction = notes;
        DiagramApiController.saveReset(psf_ProtectiveFunctionComments).then(
          dd => this.setDiagramData(dd)
        );
        break;
      }
      case "Shutdown": {
        psf_ProtectiveFunctionComments.shutdownDescription = notes;
        DiagramApiController.saveShutdown(psf_ProtectiveFunctionComments).then(
          dd => this.setDiagramData(dd)
        );
        break;
      }
      case "Reference": {
        psf_ProtectiveFunctionComments.referDocuments = notes;
        DiagramApiController.saveReference(psf_ProtectiveFunctionComments).then(
          dd => this.setDiagramData(dd)
        ); 
        break;
      }
      case "Comments": {
        psf_ProtectiveFunctionComments.sourceComments = notes;
        DiagramApiController.saveComments(psf_ProtectiveFunctionComments).then(
          dd => this.setDiagramData(dd)
        );
        break;
      }
    }
  };

  // Handle Pan and Zoom
  handlePanAndZoomOneUp = ( scale: number) => {
    this.setState({ scale });
  }

  
  render() {
    return (
      <div className="grid-container"
        onMouseMove={this.handleUserInteraction}
        onKeyDown={this.handleUserInteraction}
        onScroll={this.handleUserInteraction}
      >
        <header className={`header ${this.state.readOnly || this.state.isLocked ? "readonly" : ""}`}>
          <Header
            userName={this.state.userName}
            protectiveFunction={this.state.protectiveFunction}
            sftTargets={this.state.sftTargets}
            onCalculateButtonHandler={this.handleCalculateButtonClicked}
            calculations={this.state.calculations}
            pfdPieChartData={this.state.pfdPieChartData}
            strPieChartData={this.state.strPieChartData}
            calculationResults={this.state.calculationResults}
            hftResults={this.state.hftResults}
            handleTargetSpecUpdate={this.handleTargetSpecUpdate}
            onExitButtonClicked={this.handleExitButtonClicked}
            onForwardOrBackButtonClicked={this.handleForwardOrBackButtonPressed}
            onZoomButtonsClick={this.handleZoomButtonsClicked}
            onUpgradeButtonClick={this.handleUpgradeButtonClick}
            readOnly={this.state.readOnly || this.state.isLocked}
            area={this.state.area}
          />
        </header>
        <aside className="sidenav">
          <DeviceBar
            devices={this.state.deviceBarDevices}
            onConfigurationClick={this.handleConfigurationClick}
            onSupportConfigurationClick={this.handleSupportConfigurationClick}
            onDeviceClick={this.handleDeviceClick}
            selectedDevice={this.state.deviceRegister}
            handleProcessHazardUpdate={this.handleProcessHazardUpdate}
            createManualInput={this.createManualInput}
            createManualAction={this.createManualAction}
            deleteCustomLogic={this.deleteCustomLogic}
            deleteCustomDevice={this.deleteCustomDevice}
            deleteCustomSupport={this.deleteCustomSupport}
            saveCustomLogicSolver={this.saveCustomLogicSolver}
            saveCustomDeviceSheet={this.saveCustomDeviceSheet}
            saveCustomSupportSystem={this.saveCustomSupportSystem}
            saveSrsNotes={this.saveSrsNotes}
            psf_ProtectiveFunctionComments={
              this.state.psfProtectiveFunctionComments
            }
            customDeviceUsageInfo={this.state.customDeviceUsageInfo}
            wipeCustomDevicePt_ProtectiveFunction={this.wipeCustomDevicePt_ProtectiveFunction}
            promoteToClientLevel={this.promoteToClientLevel}
            userDetails={this.state.userDetails}
          />
        </aside>
        <main className="main">
          <Diagram
            deleteConfiguration={this.deleteConfiguration}
            copyBoundDevice={this.copyBoundDevice}
            onPasteBoundDevice={this.pasteBoundDevice}
            deleteDevice={this.deleteDevice}
            deleteManualInput={this.deleteManualInput}
            deleteManualAction={this.deleteManualAction}
            devices={this.state.devices}
            handleEmptySupportDeviceLocationClick={
              this.addSupportSystem
            }
            handleEmptyDeviceLocationClick={this.handleEmptyDeviceLocationClick}
            handleEmptyLogicDeviceLocationClick={
              this.handleEmptyLogicDeviceLocationClick
            }
            handleUpdateLogicSolverDataClick={this.handleUpdateLogicSolverData}
            loading={this.state.loading}
            onInputRedundancyChange={this.handleInputRedundancyChange}
            onManualActionSave={this.handleManualActionSave}
            onManualInputsSave={this.handleManualInputsSave}
            onUpdateCcf={this.handleUpdateCcf}
            onUpdateDeviceSheetData={this.handleUpdateDeviceSheetData}
            manualAction={this.state.manualAction}
            manualInputs={this.state.manualInputs}
            psfProtectiveFunctionComments={
              this.state.psfProtectiveFunctionComments
            }
            pasteConfiguration={this.pasteConfiguration}
            redundancy={this.state.protectiveFunction.redundant}
            zoom={this.state.zoom}
            supportConfigPasteState={this.state.supportConfigPasteState}
            errorsAndWarnings={this.state.errorsAndWarnings}
            setDiagramData={this.setDiagramData}
            acceptCalculationResult={() =>
              this.setState({ showErrorsAndWarningsModal: false })
            }
            handlePanAndZoomOneUp={this.handlePanAndZoomOneUp}
            scale={this.state.scale}
            showErrorsAndWarningsModal={this.state.showErrorsAndWarningsModal}
            masterPoolMismatchItems={this.state.masterPoolMismatchItems}
            onRestoreDeviceSheetData={this.handleRestoreDeviceSheetData}
            readOnly={this.state.readOnly || this.state.isLocked}
          />
        </main>
        {this.state.secondsUntilExpiration <= 60 &&
          <SilModal title="Session Expiring!" closeModal={() => { }}>
            <div>
              <div>
                <b>Session will expire in {this.state.secondsUntilExpiration} seconds.
                <br />
                  Do you want to extend your time?
                </b>
              </div>
            <div className="pull-right" style={{marginTop: "20px"}}>
              <div
                id="reset"
                className="btn"
                onClick={() => DiagramApiController.getDiagramData(this.props.protectiveFunctionId).then(dd => this.setDiagramData(dd))}
              >Yes</div>
              <div
                className="btn btn-default"
                onClick={() => window.location.href = "/Login.aspx"}>No</div>
              </div>
            </div>
          </SilModal>
        }
      </div>
    );
  }
}
